/**
 * veluna-consent.js — ONE canonical consent state for every marketing/analytics integration.
 * v2 · 2026-09-11 (replaces the 2026-09-06 signal layer; same public API, plus purposes).
 *
 * Source of truth = Shopify Customer Privacy (Settings → Customer privacy). This file is a thin
 * adapter; it does not build a parallel consent system and renders no UI. Shopify's own cookie
 * banner (when enabled for a region) collects the choice and stores it; Shopify applies it to its
 * managed surfaces (web pixels: Google & YouTube, TikTok partner, Pinterest, Klaviyo; audiences;
 * checkout). Theme-loaded integrations read the SAME state through this adapter.
 *
 * Purposes (Shopify's four):
 *   analytics    → analyticsAllowed()     GA-style measurement loaded by the theme (none today)
 *   marketing    → marketingAllowed()     Meta pixel init, Meta relay, any ad SDK/event, audiences
 *   preferences  → preferencesAllowed()   language/currency/size memory (theme first-party)
 *   sale_of_data → saleOfDataAllowed()    sharing identifiers with third parties for advertising
 *                                          (hashed email/phone, click ids). GPC forces this false.
 *   necessary commerce functionality (cart, checkout, order webhooks, fraud, first-party
 *   attribution written to cart attributes) is NOT gated and must not call this API.
 *
 * Decision order (identical for every caller):
 *   1. Global Privacy Control (navigator.globalPrivacyControl === true)
 *        → marketing DENY, sale_of_data DENY (analytics/preferences unaffected). Always, everywhere.
 *   2. Shopify Customer Privacy API answers (loaded via Shopify.loadFeatures 'consent-tracking-api')
 *        → follow them exactly. In regions where the merchant requires consent, Shopify returns
 *          false for non-essential purposes until the visitor decides; elsewhere it returns true.
 *   3. API not loaded yet / unavailable → provisional DEFAULT below, then re-decide on load.
 *
 * DEFAULT_WHEN_UNKNOWN: 'allow' reproduces Shopify's own behaviour for regions where consent is
 * not required (the store's documented model: US/CA opt-out, GPC honoured). Set to 'deny' to hold
 * marketing until the API answers (a few hundred ms) — this is the only knob, and it is documented.
 */
(function (w, d) {
  'use strict';

  var DEFAULT_WHEN_UNKNOWN = 'allow';           // 'allow' | 'deny' — see header
  var STATE = { source: 'default', marketing: null, analytics: null, preferences: null, saleOfData: null };
  var listeners = [];

  function gpc() {
    try { return w.navigator && w.navigator.globalPrivacyControl === true; } catch (e) { return false; }
  }

  function api() {
    try { return (w.Shopify && w.Shopify.customerPrivacy) || null; } catch (e) { return null; }
  }

  function ask(cp, fn) {
    try { var v = cp[fn](); return (v === true || v === false) ? v : null; } catch (e) { return null; }
  }

  function decide() {
    var cp = api();
    var dflt = DEFAULT_WHEN_UNKNOWN === 'allow';
    var s = { source: cp ? 'shopify' : 'default', marketing: dflt, analytics: dflt, preferences: true, saleOfData: dflt };
    if (cp) {
      var m = ask(cp, 'marketingAllowed'), a = ask(cp, 'analyticsProcessingAllowed'),
          p = ask(cp, 'preferencesProcessingAllowed'), sd = ask(cp, 'saleOfDataAllowed');
      if (m !== null) s.marketing = m;
      if (a !== null) s.analytics = a;
      if (p !== null) s.preferences = p;
      if (sd !== null) s.saleOfData = sd;
    }
    if (gpc()) { s.marketing = false; s.saleOfData = false; s.source = (cp ? 'shopify' : 'default') + '+gpc'; }
    STATE = s;
    return s;
  }

  function reason() {
    var s = decide();
    if (gpc()) return 'gpc_opt_out';
    if (s.source === 'shopify') return s.marketing ? 'shopify_consent_granted' : 'shopify_consent_denied';
    return DEFAULT_WHEN_UNKNOWN === 'allow' ? 'default_no_signal' : 'default_hold';
  }

  function notify() {
    var s = decide();
    for (var i = 0; i < listeners.length; i++) { try { listeners[i](s); } catch (e) { /* no-op */ } }
  }

  /* Load Shopify's Customer Privacy API. Shopify does not load it for the theme automatically. */
  function loadShopifyApi() {
    try {
      if (api()) { notify(); return; }
      if (w.Shopify && typeof w.Shopify.loadFeatures === 'function') {
        w.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function () { notify(); });
      }
    } catch (e) { /* fall back to default */ }
  }

  decide();
  loadShopifyApi();
  try { d.addEventListener('visitorConsentCollected', notify); } catch (e) { /* no-op */ }

  var apiOut = {
    /* Purpose gates — every marketing/analytics integration asks one of these. */
    marketingAllowed:   function () { return decide().marketing; },
    analyticsAllowed:   function () { return decide().analytics; },
    preferencesAllowed: function () { return decide().preferences; },
    saleOfDataAllowed:  function () { return decide().saleOfData; },
    /* Identifier sharing (hashed email/phone, click ids to ad platforms) needs BOTH. */
    identifierSharingAllowed: function () { var s = decide(); return s.marketing && s.saleOfData; },
    reason: reason,
    state: function () { return decide(); },
    /* Run fn now if marketing is allowed, else when consent later becomes allowed. */
    ifAllowed: function (fn) {
      try { if (decide().marketing) { fn(); return true; } } catch (e) { /* swallow */ }
      return false;
    },
    onChange: function (fn) { if (typeof fn === 'function') listeners.push(fn); },
    /* Observability without a network call: rolling local tally, never leaves the browser. */
    note: function (event) {
      try {
        var k = 'veluna_consent_tally';
        var t = JSON.parse(w.localStorage.getItem(k) || '{}');
        var s = decide();
        var bucket = s.marketing ? 'allowed' : 'blocked';
        t[bucket] = t[bucket] || {};
        t[bucket][event] = (t[bucket][event] || 0) + 1;
        t.reason = reason();
        t.source = s.source;
        w.localStorage.setItem(k, JSON.stringify(t));
      } catch (e) { /* storage unavailable; tally is best-effort only */ }
    }
  };

  w.VelunaConsent = apiOut;
})(window, document);
